import { useState } from "react"
import { Platform, PermissionsAndroid } from "~/ui/primitives"
import * as FileSystem from "~/ui/expo-file-system"
import * as Sharing from "expo-sharing"
import { FileSystemDownloadResult } from "~/ui/expo-file-system"
import { DownloadDirectoryPath, writeFile, mkdir } from "~/ui/fs"
import { alertError, alertSuccess, alertWarning } from "~utils/alert"
import { downloadFileInBrowser } from "~utils/h5-download"
import { staticUrl } from "~utils/url"

const { StorageAccessFramework } = FileSystem

const downloadPath = FileSystem.documentDirectory
const isAndroid = Platform.OS === "android"
// api level 33 is android 13
const isBelowAndroid13 = Number(Platform.Version) < 33

export const getAndroidExternalStoragePermissions = async () => {
  if (Platform.OS === "android") {
    if (isBelowAndroid13) {
      try {
        await PermissionsAndroid.requestMultiple([
          PermissionsAndroid.PERMISSIONS.READ_EXTERNAL_STORAGE,
          PermissionsAndroid.PERMISSIONS.WRITE_EXTERNAL_STORAGE,
        ])
        const readable = await PermissionsAndroid.check(
          PermissionsAndroid.PERMISSIONS.READ_EXTERNAL_STORAGE,
        )
        const writable = await PermissionsAndroid.check(
          PermissionsAndroid.PERMISSIONS.WRITE_EXTERNAL_STORAGE,
        )
        const granted = readable && writable
        return granted
      } catch (err) {
        return false
      }
    } else {
      return true
    }
  } else {
    return true
  }
}

const useFileSystem = () => {
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState(0)

  const downloadCallback = (downloadProgress) => {
    const progress = downloadProgress.totalBytesWritten / downloadProgress.totalBytesExpectedToWrite
    setDownloadProgress(progress)
  }

  const ensureDirAsync = async (dir, intermediates = true) => {
    const props = await FileSystem.getInfoAsync(dir)
    if (props.exists && props.isDirectory) {
      return props
    }
    await FileSystem.makeDirectoryAsync(dir, { intermediates })
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return await ensureDirAsync(dir, intermediates)
  }

  const saveIosFile = async (downloadResult: FileSystemDownloadResult, mimeType) => {
    const canShare = await Sharing.isAvailableAsync()
    if (canShare) {
      Sharing.shareAsync(downloadResult.uri, {
        UTI: mimeType,
      })
    }
  }

  const saveAndroidFile = async (downloadResult: FileSystemDownloadResult, fileName, mimeType) => {
    try {
      let granted: boolean
      let destination: string
      if (isBelowAndroid13) {
        granted = await getAndroidExternalStoragePermissions()
        destination = DownloadDirectoryPath
      } else {
        const permissionsResult = await StorageAccessFramework.requestDirectoryPermissionsAsync()
        granted = permissionsResult.granted
        if (permissionsResult.granted) {
          destination = permissionsResult.directoryUri
        }
      }

      if (!granted) {
        alertWarning({ message: "请至设定允许储存/写入权限" })
        return
      }

      const { uri } = downloadResult
      const fileString = await FileSystem.readAsStringAsync(uri, {
        encoding: FileSystem.EncodingType.Base64,
      })

      const newFileName = `${fileName.split(".")[0]}-${Date.now()}`
      const extension = fileName.split(".")[1]

      if (isBelowAndroid13) {
        await mkdir(destination)
        await writeFile(
          destination + "/" + newFileName + "." + extension,
          fileString,
          FileSystem.EncodingType.Base64,
        )
        alertSuccess({ message: "下载成功" })
      } else {
        await StorageAccessFramework.createFileAsync(destination, newFileName, mimeType).then(
          async (uri) => {
            await FileSystem.writeAsStringAsync(uri, fileString, {
              encoding: FileSystem.EncodingType.Base64,
            })
            alertSuccess({ message: "下载成功" })
          },
        )
      }
    } catch (err) {
      alertError({ message: err.message })
    }
  }

  const downloadFile = async (fileUrl, mimeType) => {
    setIsDownloading(true)

    // H5：浏览器直接下载
    if (Platform.OS === "web") {
      try {
        const url = staticUrl(fileUrl)
        const fileName = url.split("/").pop()?.split("?")[0] || "download"
        await downloadFileInBrowser(url, fileName)
        alertSuccess({ message: "已开始下载" })
      } catch (e) {
        alertError({ message: e instanceof Error ? e.message : "下载失败" })
      }
      setIsDownloading(false)
      return
    }

    if (isAndroid) {
      ensureDirAsync(downloadPath)
    }

    const fileName = fileUrl.split("/").reverse()[0]
    const downloadResumable = FileSystem.createDownloadResumable(
      fileUrl,
      downloadPath + fileName,
      {},
      downloadCallback,
    )

    try {
      const downloadResult = await downloadResumable.downloadAsync()
      if (isAndroid) saveAndroidFile(downloadResult, fileName, mimeType)
      else saveIosFile(downloadResult, mimeType)
    } catch (e) {
      alertError({ message: e.message })
    }
    setIsDownloading(false)
  }

  return {
    isDownloading,
    downloadProgress,
    downloadFile,
  }
}

export default useFileSystem
